import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import GetSurveys from "../queries/GetSurveys";
import Select from "react-select";
import Spinner from "./Spinner";
import writeXlsxFile from "write-excel-file";
import { useAppContext } from "../AppContext";

import {
  Table,
  Row,
  Col,
  Card,
  CardHeader,
  FormGroup,
  CardBody,
  Button,
} from "reactstrap";

const LikertRatings = () => {
  const imi_questions = [
    {
      key: "ease_of_access",
      type: "likert",
      default: null,
      text: "The courseware was easy to access.",
      required: true,
      errorText: "Please make a selection.",
    },
    {
      key: "ease_of_navigation",
      type: "likert",
      default: null,
      text: "It was easy to navigate through the module.",
    },
    {
      key: "clear_instructions",
      type: "likert",
      default: null,
      text: "Instructions were clear and easy to follow, both in the target language and in English.",
    },
    {
      key: "understood_expectation",
      type: "likert",
      default: null,
      text: "I understood what I was expected to know or be able to do at the end of the module.",
    },
    {
      key: "interesting_manner",
      type: "likert",
      default: null,
      text: "The module content was presented in an interesting manner.",
    },
    {
      key: "pe_realistic",
      type: "likert",
      default: null,
      text: "The practical exercise scenarios were realistic and job-relevant for the FAO audience.",
    },
    {
      key: "pe_useful",
      type: "likert",
      default: null,
      text: "The practical exercises provided useful opportunity to practice applying terms and concepts introduced elsewhere in the module.",
    },
    {
      key: "balance_pe_vs_course",
      type: "likert",
      default: null,
      text: "There was an appropriate balance between time spent on practical exercises and on other kinds of learning activities (e.g., readings, vocabulary exercises, etc.).",
    },
    {
      key: "instructions_appropriate",
      type: "likert",
      default: null,
      text: "The overall level of the instruction seemed appropriate for FAOs at an ILR Level of 3/3/2, minimum 2/2/2.",
    },
    {
      key: "reading_listening_appropriate",
      type: "likert",
      default: null,
      text: "The reading/listening activities were appropriate for the FAO audience.",
    },
    {
      key: "time_taken",
      type: "likertInputCustom",
      default: null,
      text: "The time it took me to complete this module was:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Less than 4 hours", value: "<4" },
        { text: "4 to less than 5 hours", value: "4-5" },
        { text: "5 to less than 6 hours", value: "5-6" },
        { text: "6 to less than 7 hours", value: "6-7" },
        { text: "7 or more hours", value: ">7" },
      ],
    },
    {
      key: "module_length",
      type: "likertInputCustom",
      default: null,
      text: "The module was:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Too Long", value: "too_long" },
        { text: "Too Short", value: "too_short" },
        { text: "About Right", value: "about_right" },
      ],
    },
    {
      key: "overall_assessment",
      type: "likertInputCustom",
      default: null,
      text: "My overall assessment of the module is:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Excellent", value: "excellent" },
        { text: "Good", value: "good" },
        { text: "Average", value: "average" },
        { text: "Fair", value: "fair" },
        { text: "Poor", value: "poor" },
      ],
    },
    {
      key: "improved_proficiency",
      type: "likert",
      default: null,
      text: "This module will improve my language proficiency.",
    },
    {
      key: "improved_job_performance",
      type: "likert",
      default: null,
      text: "This module will improve my language-related job performance.",
    },
  ];

  const test_questions = [
    {
      key: "ease_of_access",
      type: "likert",
      default: null,
      text: "The module assessment (or course posttest) was easy to access.",
    },
    {
      key: "ease_of_navigation",
      type: "likert",
      default: null,
      text: "It was easy to navigate through the module assessment (or course posttest).",
    },
    {
      key: "clear_instructions",
      type: "likert",
      default: null,
      text: "Instructions for the module assessment (or course posttest) questions were clear and easy to follow, both in the target language and in English.",
    },
    {
      key: "focused_on_material",
      type: "likert",
      default: null,
      text: "The module assessment (or course posttest) focused on content that was taught in the associated module.",
    },
    {
      key: "module_length",
      type: "likertInputCustom",
      default: null,
      text: "The module assessment (or course posttest) was:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Too Long", value: "too_long" },
        { text: "Too Short", value: "too_short" },
        { text: "About Right", value: "about_right" },
      ],
    },
    {
      key: "overall_assessment",
      type: "likertInputCustom",
      default: null,
      text: "My overall evaluation of the module assessment (or course posttest) is:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Excellent", value: "excellent" },
        { text: "Good", value: "good" },
        { text: "Average", value: "average" },
        { text: "Fair", value: "fair" },
        { text: "Poor", value: "poor" },
      ],
    },
  ];
  const { error, loading, data, refetch } = useQuery(GetSurveys, {
    variables: {
      survey_language: null,
      trial_type: null,
      survey_type: null,
    },
  });
  const [selectedLanguage, setSelectedLanguage] = useState({
    value: null,
    label: "None",
  });
  const [selectedTrialType, setSelectedTrialType] = useState({
    value: null,
    label: "None",
  });
  const [selectedSurveyType, setSelectedSurveyType] = useState({
    value: null,
    label: "None",
  });
  const dataToDisplay = useRef([]);
  const { displayToast, closeToast } = useAppContext();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const runRefetch = async () => {
      let variables;

      if (
        (!selectedLanguage || selectedLanguage.value === null) &&
        (!selectedTrialType || selectedTrialType.value === null) &&
        (!selectedSurveyType || selectedSurveyType.value === null)
      ) {
        variables = {
          survey_language: null,
          trial_type: null,
          survey_type: null,
        };
      } else {
        variables = {
          survey_language: selectedLanguage?.value ?? null,
          trial_type: selectedTrialType?.value ?? null,
          survey_type: selectedSurveyType?.value ?? null,
        };
      }

      dataToDisplay.current = null;
      setIsFetching(true);

      try {
        await refetch(variables);
      } finally {
        setIsFetching(false);
      }
    };

    runRefetch();
  }, [selectedLanguage, selectedTrialType, selectedSurveyType, refetch]);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  const { surveys } = data;

  // set up what data is to be displayed here now that surveys are available
  if (
    (dataToDisplay.current == null || dataToDisplay.current.length === 0) &&
    surveys.length !== 0
  ) {
    dataToDisplay.current = [];

    surveys.forEach((survey, sIndex) => {
      if (!checkCompletionStatus(survey)) return;

      const existingData = dataToDisplay.current.find(
        (data) => data.title === survey.title,
      );

      const thisData = existingData
        ? existingData
        : {
            title: survey.title,
            likerts: [],
            customLikerts: [],
          };

      let questionsToCheck = [];

      if (
        survey.survey_type === null ||
        survey.survey_type === "imi" ||
        survey.survey_type === "introduction"
      ) {
        questionsToCheck = imi_questions;
      } else {
        questionsToCheck = test_questions;
      }

      questionsToCheck.forEach((question, qIndex) => {
        if (question.type === "likert") {
          const existingLikert = thisData.likerts.find(
            (likert) => likert.text === question.text,
          );

          // Function to add rank data to likert object
          const addRankData = (likert, rankValue, userName) => {
            switch (rankValue) {
              case 5:
                likert.rank_5.push(userName);
                break;
              case 4:
                likert.rank_4.push(userName);
                break;
              case 3:
                likert.rank_3.push(userName);
                break;
              case 2:
                likert.rank_2.push(userName);
                break;
              case 1:
                likert.rank_1.push(userName);
                break;
              default:
                likert.no_rank.push(userName);
                break;
            }
          };

          if (existingLikert) {
            // Likert question already exists; add data to it
            addRankData(
              existingLikert,
              survey[question.key],
              survey.user.user_name,
            );
          } else {
            // Likert question doesn't exist; create a new object and push it
            const likert = {
              text: question.text,
              rank_5: [],
              rank_4: [],
              rank_3: [],
              rank_2: [],
              rank_1: [],
              no_rank: [],
            };

            addRankData(likert, survey[question.key], survey.user.user_name);

            thisData.likerts.push(likert);
          }
        } else if (question.type === "likertInputCustom") {
          const existingCustomLikert = thisData.customLikerts.find(
            (cusomLikert) => {
              return cusomLikert.text === question.text;
            },
          );

          if (existingCustomLikert) {
            console.log(`Exists: ${survey.title}`);
            question.fields.forEach((field, fIndex) => {
              if (
                existingCustomLikert.fields[fIndex].value ===
                survey[question.key]
              ) {
                console.log("trying to push: ", survey.user.user_name);
                if (!existingCustomLikert.fields[fIndex].users) {
                  existingCustomLikert.fields[fIndex].users = [];
                }
                existingCustomLikert.fields[fIndex].users.push(
                  survey.user.user_name,
                );
              }
            });
          } else {
            const customLikert = {
              text: question.text,
            };

            customLikert.fields = [];
            question.fields.forEach((field, fIndex) => {
              customLikert.fields[fIndex] = JSON.parse(JSON.stringify(field));
              if (customLikert.fields[fIndex].value === survey[question.key]) {
                customLikert.fields[fIndex].users = [survey.user.user_name];
              }
            });

            thisData.customLikerts.push(customLikert);
          }
        } else {
          console.log("this type of question is: ", question.type);
        }
      });

      if (!existingData) dataToDisplay.current.push(thisData);
    });

    dataToDisplay.current.sort((a, b) => {
      const languageA = getLanguage(a.title);
      const languageB = getLanguage(b.title);
      const priorityA = getContentPriority(a.title);
      const priorityB = getContentPriority(b.title);

      if (languageA !== languageB) {
        return languageA.localeCompare(languageB); // Sort by language
      }

      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Sort by content priority
      }

      // If the same category, sort titles alphabetically
      return a.title.localeCompare(b.title);
    });
  }

  // Function to assign priority to different course components
  function getContentPriority(title) {
    if (title.includes("Introduction")) return 1;
    if (title.includes("Module")) return 2;
    if (title.includes("Posttest")) return 3;
    return 4;
  }

  // Extract language from title
  function getLanguage(title) {
    if (title.includes("Arabic")) return "Arabic";
    if (title.includes("Russian")) return "Russian";
    if (title.includes("Korean")) return "Korean";
    return "Other";
  }

  function checkCompletionStatus(survey) {
    if (survey.completed_date != null) {
      return true;
    }

    return false;
  }

  function displayCustomLikerts(likerts, dIndex) {
    return likerts.map((likert, lIndex) => {
      let totalVotes = 0;
      likert.fields.forEach((field) => {
        if (field.users && field.users.length > 0) {
          totalVotes = totalVotes + field.users.length;
        }
      });
      return (
        <tbody key={`likert_${dIndex}_${lIndex}`}>
          <tr>
            <td>Question</td>
            {likert.fields.map((field, fIndex) => (
              <td key={`customLikertHeader_${dIndex}_${lIndex}_${fIndex}`}>
                {field.text}
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ whiteSpace: "normal" }}>{likert.text}</td>
            {likert.fields.map((field, fIndex) => (
              <td
                key={`customLikertData_${dIndex}_${lIndex}_${fIndex}`}
                style={{ whiteSpace: "normal" }}
              >
                {renderLikertColumn(field.users, totalVotes)}
              </td>
            ))}
          </tr>
        </tbody>
      );
    });
  }

  function displayLikerts(likerts, dIndex) {
    return (
      <tbody>
        <tr>
          <th>Question</th>
          <th>Strongly Agree</th>
          <th>Agree</th>
          <th>Neither Agree or Disagree</th>
          <th>Disagree</th>
          <th>Strongly Disagree</th>
        </tr>
        {likerts.map((likert, lIndex) => {
          const totalVotes =
            (likert.rank_5 ? likert.rank_5.length : 0) +
            (likert.rank_4 ? likert.rank_4.length : 0) +
            (likert.rank_3 ? likert.rank_3.length : 0) +
            (likert.rank_2 ? likert.rank_2.length : 0) +
            (likert.rank_1 ? likert.rank_1.length : 0);

          return (
            <tr key={`likert_${dIndex}_${lIndex}`}>
              <td style={{ whiteSpace: "normal" }}>{likert.text}</td>
              <td style={{ whiteSpace: "normal" }}>
                {renderLikertColumn(likert.rank_5, totalVotes)}
              </td>
              <td style={{ whiteSpace: "normal" }}>
                {renderLikertColumn(likert.rank_4, totalVotes)}
              </td>
              <td style={{ whiteSpace: "normal" }}>
                {renderLikertColumn(likert.rank_3, totalVotes)}
              </td>
              <td style={{ whiteSpace: "normal" }}>
                {renderLikertColumn(likert.rank_2, totalVotes)}
              </td>
              <td style={{ whiteSpace: "normal" }}>
                {renderLikertColumn(likert.rank_1, totalVotes)}
              </td>
            </tr>
          );
        })}
      </tbody>
    );
  }

  function renderLikertColumn(users, totalVotes) {
    if (!users || users.length === 0) {
      return <div>0%</div>;
    }

    const percentage = (users.length / totalVotes) * 100;
    return (
      <>
        <div>{percentage.toFixed(2)}%</div>
        {users.map((user, index) => (
          <div key={index}>{user}</div>
        ))}
      </>
    );
  }

  function displayData() {
    return dataToDisplay.current.map((data, dIndex) => {
      // return MyTable(data)

      return (
        <React.Fragment key={`dataToDisplay_${dIndex}`}>
          <Table bordered striped>
            <thead>
              <tr>
                <th
                  scope="col"
                  style={{ backgroundColor: "lightgray" }}
                  colSpan={6}
                >
                  <div
                    className="d-flex justify-content-center font-weight-bold"
                    style={{
                      fontSize: ".85rem",
                    }}
                  >
                    {data.title}
                  </div>
                </th>
              </tr>
            </thead>
            {displayLikerts(data.likerts, dIndex)}
            {displayCustomLikerts(data.customLikerts, dIndex)}
          </Table>
        </React.Fragment>
      );
    });
  }

  function generateExcel() {
    let success =
      dataToDisplay.current !== null && dataToDisplay.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    console.log("dataToDisplay: ", dataToDisplay.current);

    let tableData = [];

    dataToDisplay.current.forEach((data, dIndex) => {
      // objects.push(data.problems);
      // schemas.push(schema)
      // sheets.push(data.title.replace(/^Course I, /, '').slice(0, 30));

      tableData.push([
        {
          value: data.title,
          span: 6,
          backgroundColor: "#484a5c",
          color: "#ffffff",
          align: "center",
          width: 72,
        },
        null,
        null,
        null,
        null,
        null,
      ]);

      tableData.push([
        {
          type: String,
          value: "Question",
          align: "center",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
        {
          type: String,
          value: "Strongly Agree",
          align: "center",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
        {
          type: String,
          value: "Agree",
          align: "center",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
        {
          type: String,
          value: "Neither Agree or Disagree",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
          wrap: true,
        },
        {
          type: String,
          value: "Disagree",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
        {
          type: String,
          value: "Strongly Disagree",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
      ]);

      data.likerts.forEach((likert, lIndex) => {
        const { rank_1, rank_2, rank_3, rank_4, rank_5 } = likert;
        const total_submissions =
          rank_1.length +
          rank_2.length +
          rank_3.length +
          rank_4.length +
          rank_5.length;

        let stronglyAgreeCellData =
          Math.round((rank_5.length / total_submissions) * 100).toString() +
          "%";
        let agreeCellData =
          Math.round((rank_4.length / total_submissions) * 100).toString() +
          "%";
        let neitherCellData =
          Math.round((rank_3.length / total_submissions) * 100).toString() +
          "%";
        let disagreeCellData =
          Math.round((rank_2.length / total_submissions) * 100).toString() +
          "%";
        let stronglyDisagreeCellData =
          Math.round((rank_1.length / total_submissions) * 100).toString() +
          "%";

        // NOTES ABOUT THE || IN THE DATA!!!!
        // EASIEST WAY TO REPLACE IT WITH NEW LINE IN EXCEL IS BY DOING THE FOLLOWING
        // PRESS CTRL-H
        // IN THE TOP PUT ||
        // IN THE BOTTOM HOLD ALT AND CLICK O10 ON THE NUMBER PAD
        // CLICK REPLACE ALL

        if (rank_5.length > 0) {
          rank_5.forEach((user_name, rIndex) => {
            stronglyAgreeCellData = `${stronglyAgreeCellData}||${user_name}`;
          });
        }
        if (rank_4.length > 0) {
          rank_4.forEach((user_name, rIndex) => {
            agreeCellData = `${agreeCellData}||${user_name}`;
          });
        }
        if (rank_3.length > 0) {
          rank_3.forEach((user_name, rIndex) => {
            neitherCellData = `${neitherCellData}||${user_name}`;
          });
        }
        if (rank_2.length > 0) {
          rank_2.forEach((user_name, rIndex) => {
            disagreeCellData = `${disagreeCellData}||${user_name}`;
          });
        }
        if (rank_1.length > 0) {
          rank_1.forEach((user_name, rIndex) => {
            stronglyDisagreeCellData = `${stronglyDisagreeCellData}||${user_name}`;
          });
        }

        tableData.push([
          {
            type: String,
            value: likert.text,
            width: 12,
          },
          {
            type: String,
            value:
              stronglyAgreeCellData === "0%" ? null : stronglyAgreeCellData,
            width: 12,
            align: "center",
          },
          {
            type: String,
            value: agreeCellData === "0%" ? null : agreeCellData,
            width: 12,
            align: "center",
          },
          {
            type: String,
            value: neitherCellData === "0%" ? null : neitherCellData,
            width: 12,
            align: "center",
          },
          {
            type: String,
            value: disagreeCellData === "0%" ? null : disagreeCellData,
            width: 12,
            align: "center",
          },
          {
            type: String,
            value:
              stronglyDisagreeCellData === "0%"
                ? null
                : stronglyDisagreeCellData,
            width: 12,
            align: "center",
          },
        ]);
      });

      data.customLikerts.forEach((cLikert, cIndex) => {
        const { fields } = cLikert;

        // first add the row that says questions and the field labels
        let rowHeader = [
          {
            type: String,
            value: "Question",
            width: 12,
            backgroundColor: "#a8aabd",
            color: "#ffffff",
          },
        ];

        fields.forEach((field, fIndex) => {
          rowHeader.push({
            type: String,
            value: field.text,
            width: 12,
            backgroundColor: "#a8aabd",
            color: "#ffffff",
          });
        });

        tableData.push(rowHeader);

        // second add the row with the actual data
        const total_submissions = fields.reduce(
          (acc, field) =>
            acc + (Array.isArray(field.users) ? field.users.length : 0),
          0,
        );

        let rowData = [
          {
            type: String,
            value: cLikert.text,
            width: 12,
          },
        ];

        fields.forEach((field, fIndex) => {
          let percentage = "0%";
          if (total_submissions > 0 && Array.isArray(field.users)) {
            percentage =
              Math.round(
                (field.users.length / total_submissions) * 100,
              ).toString() + "%";
          }

          const objectToPush = {
            type: String,
            value: percentage === "0%" ? null : percentage,
            width: 12,
            align: "center",
          };

          if (percentage !== "0%") {
            field.users.forEach((user, uIndex) => {
              objectToPush.value = `${objectToPush.value}||${user}`;
            });
          }

          rowData.push(objectToPush);
        });

        tableData.push(rowData);
      });
    });

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `likerts_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  return (
    <>
      <Button color="primary" onClick={() => generateExcel()}>
        Create Excel
      </Button>
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-language">
                  Language
                </label>
                <Select
                  value={selectedLanguage}
                  onChange={(selectedLanguage) => {
                    setSelectedLanguage(selectedLanguage);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "arabic",
                      label: "Arabic",
                    },
                    {
                      value: "korean",
                      label: "Korean",
                    },
                    {
                      value: "russian",
                      label: "Russian",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-language"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-trial">
                  Trial Type
                </label>
                <Select
                  value={selectedTrialType}
                  onChange={(selectedTrialType) => {
                    setSelectedTrialType(selectedTrialType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "individual",
                      label: "Individual",
                    },
                    {
                      value: "group",
                      label: "Group",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-trial"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-survey">
                  Survey Type
                </label>
                <Select
                  value={selectedSurveyType}
                  onChange={(selectedSurveyType) => {
                    setSelectedSurveyType(selectedSurveyType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "imi",
                      label: "IMI",
                    },
                    {
                      value: "introduction",
                      label: "Introduction",
                    },
                    {
                      value: "assessment",
                      label: "Assessment",
                    },
                    {
                      value: "posttest",
                      label: "Posttest",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-survey"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {dataToDisplay.current !== null &&
          dataToDisplay.current.length > 0 ? (
            <>
              <div className="d-flex justify-content-center py-2 w-100 font-weight-bold table-dark">
                Course II
              </div>
              {/* <div style={{overflowX: "auto", maxWidth: "100%", width: "100%", marginBottom: "1rem"}}> */}
              <div className="table-responsive">{displayData()}</div>
            </>
          ) : isFetching ? (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <Spinner />
              <h3 className="text-muted mt-3">Loading results...</h3>
            </div>
          ) : (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <h1>
                <i className="ni ni-bell-55 text-muted" />
              </h1>
              <h3 className="text-muted">
                Please select what you'd like to view
              </h3>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default LikertRatings;
